import { Alert, LoadingBlock, cx } from "./ui";
import { useTranslation } from "react-i18next";
import { formatRate } from "../lib/money";
import type { Plan } from "../lib/types";

/**
 * Mirrors the server's derivation so the row shows the rate that will actually
 * be charged. 28 × 24 = 672 — the shortest month — and the server floors for
 * the same reason: under-collect by a fraction rather than over-collect.
 *
 * Because the divisor is the shortest month, the monthly figure beside this is
 * what a full month costs in every month, not a ceiling only long months reach.
 * The footnote below says so.
 */
const HOURS_PER_MONTH = 672;
const hourlyMicros = (priceCents: number) => Math.floor((priceCents * 10_000) / HOURS_PER_MONTH);

/**
 * The tier an app or database runs on.
 *
 * One row per plan rather than a grid of cards: more plans are coming, and a
 * list keeps them in a single scannable column however many are added, where
 * cards would wrap into a second rank that no longer compares side by side.
 * The differences between tiers are a small table of numbers, so each row
 * carries them inline and the eye compares down the columns.
 *
 * The row is a single line at every size: identity on the left, the spec as a
 * "·"-joined string beside it, price on the right — the description appears
 * only where the width can hold it, and the whole row wraps gracefully when a
 * phone cannot.
 */
export function PlanPicker({
  plans,
  loading,
  value,
  onChange,
}: {
  plans: Plan[];
  loading: boolean;
  value: string | null;
  onChange: (planId: string) => void;
}) {
  const { t } = useTranslation("create");
  if (loading) return <LoadingBlock label={t("plans.loading")} />;

  if (plans.length === 0) {
    return (
      <Alert tone="warning">
        {t("plans.none")}
      </Alert>
    );
  }

  return (
    <div role="radiogroup" aria-label="Plan" className="card divide-y divide-line">
      {plans.map((plan) => {
        const selected = plan.id === value;

        /*
         * What the row says about itself, once.
         *
         * `plan.name` is the specification on every current tier — "1 vCPU ·
         * 1 GB" — and this used to print that name and then print CPU and
         * memory again in a column beside it. Every app row said the same six
         * characters twice, and on the database rows the duplicate column won
         * the width fight and truncated the name to "1 vCPU · 1 …" while the
         * figures it had dropped sat intact two columns to the right.
         *
         * So the name leads, and `detail` carries only what the name has not
         * already said: the spec on a legacy tier that carries an adjective
         * instead ("Small"), and the disk figure on a database tier. Both are
         * absent on a current application tier, which is why that row is now a
         * single line of text.
         *
         * Storage keeps its word. Nothing enforces it — see `storageGb` in
         * lib/types.ts — so a bare "15 GB" flush beside CPU and memory would
         * read as a third container limit, which is the one thing it is not.
         */
        const spec = planSpec(plan);
        const detail = [
          plan.name === spec ? null : spec,
          plan.storageGb === null ? null : t("plans.storage", { gb: plan.storageGb }),
        ]
          .filter(Boolean)
          .join(" · ");

        return (
          <button
            key={plan.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(plan.id)}
            className={cx(
              // The 2px left edge is reserved on every row and coloured on the
              // selected one. Reserving it is the point: added only when
              // selected, it would shunt that row's text 2px out of the column
              // every other row sits in.
              "grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-3 border-l-2 px-4 py-2.5 text-left transition-colors sm:gap-x-4",
              selected ? "border-l-accent bg-accent-soft" : "border-l-transparent hover:bg-raised",
            )}
          >
            {/* The radio indicator doubles as the selection mark, so the state
                is visible before the name is read. */}
            <span
              aria-hidden="true"
              className={cx(
                "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
                selected ? "border-accent" : "border-line-strong",
              )}
            >
              {selected && <span className="size-2 rounded-full bg-accent" />}
            </span>

            <span className="min-w-0">
              <span className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-sm font-medium text-fg tabular-nums">{plan.name}</span>
                {detail && (
                  <span className="text-xs text-fg-subtle tabular-nums">{detail}</span>
                )}
              </span>
              {plan.description && (
                <span className="mt-0.5 block truncate text-xs text-fg-muted">
                  {plan.description}
                </span>
              )}
            </span>

            {/*
              One block, right-aligned, two lines: the figure people compare on
              and the figure that actually leaves their balance. Aligned rather
              than merely present — a picker exists to be read down its columns,
              and $2, $10 and $640 have to end on the same edge for that
              to work at all.
            */}
            <span className="shrink-0 text-right whitespace-nowrap">
              <span className="text-sm font-semibold tracking-tight text-fg tabular-nums">
                {formatPrice(plan.price.cents, plan.price.currency)}
              </span>
              <span className="ml-1 text-xs text-fg-subtle">/mo</span>
              <span className="mt-0.5 block font-mono text-xs text-fg-subtle tabular-nums">
                {formatRate(hourlyMicros(plan.price.cents), plan.price.currency)}/hr
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

export function formatMemory(mb: number): string {
  return mb >= 1024 ? `${(mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1)} GB` : `${mb} MB`;
}

/**
 * The smallest memory a form will start someone on, in MB.
 *
 * Not a limit and not a recommendation the API makes — the catalogue has no
 * "recommended" flag and should not grow one to serve a default.
 */
export const RUNTIME_MEMORY_MB = 1024;

/**
 * Which tier a creation form should preselect.
 *
 * "The cheapest one" was the whole rule, and it was right for as long as the
 * cheapest thing sold was 1 GB: an ordinary application boots in that. Migration
 * 0102 in maxlayer-api added rungs at 512 MB and 256 MB, and memory here is a
 * real container limit rather than a stated allowance — a Node or JVM runtime
 * that crosses it is OOM-killed, not throttled. Cheapest therefore turned,
 * overnight and without anybody choosing it, into "the one tier most first apps
 * cannot boot on".
 *
 * So the rule is the cheapest tier that holds a normal runtime, which lands on
 * the same $8 tier this defaulted to before those rungs existed. Nobody is
 * upsold, nothing moved for anyone already here, and the small tiers are one
 * click away for the people who came looking for them.
 *
 * The fallback matters as much as the rule: an operator can price a catalogue
 * this knows nothing about, and a form that preselects nothing is worse than one
 * that preselects small — so with no tier over the threshold, the cheapest wins
 * as before.
 */
export function defaultPlan(plans: Plan[]): Plan | null {
  const byPrice = [...plans].sort((a, b) => a.price.cents - b.price.cents);
  return byPrice.find((plan) => plan.resources.memoryMb >= RUNTIME_MEMORY_MB) ?? byPrice[0] ?? null;
}

/** The canonical one-line rendering of a tier's advertised resources. */
export function planSpec(plan: Plan): string {
  return `${plan.resources.cpu} vCPU · ${formatMemory(plan.resources.memoryMb)}`;
}

/**
 * A plan in one line: its name, and its resources when the name omits them.
 *
 * Current tier names *are* the specification — "2 vCPU · 4 GB" — so appending
 * the same two figures would print each twice. The retired tiers from the
 * original catalogue carry adjectives instead ("Small"), and apps still running
 * on those need the numbers spelled out. Comparing against the canonical
 * rendering rather than sniffing the string keeps that decision exact.
 */
export function describePlan(plan: Plan): string {
  const spec = planSpec(plan);
  return plan.name === spec ? spec : `${plan.name} · ${spec}`;
}

export function formatPrice(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      // Whole-dollar prices read better without the trailing zeroes.
      minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}
